<template>
  <div id="annotations-editor" />
</template>

<script setup lang="ts">
import { onMounted, ref, Ref, onUnmounted, watch } from 'vue';
import { VideoEditor } from '@cloudapp/pintura-config';
import { useRouter } from 'vue-router';
import { TrackEvents, useViewerTrack } from '@/modules/viewer/composables/useViewerTrack';
import { useViewerItemStore } from '../../store/useViewerItemStore';
import { ApiUrls } from '@/Api';

const props = defineProps<{
  id?: string;
  src?: string;
}>();

const viewerItemStore = useViewerItemStore();
const { trackItemOwner } = useViewerTrack();

let editor: any = null;

const isDirty: Ref<boolean> = ref(false);
const trackTools: Ref<string[]> = ref([]);

const router = useRouter();

const bindEditor = () => {
  if (!props.id) {
    return;
  }

  editor = new VideoEditor(
    '#annotations-editor',
    {
      enableButtonClose: true,
    },
    ApiUrls.item.videoEdit.replace('{{itemId}}', props.id)
  );

  editor.render(props.src).then((payload: any) => {
    trackItemOwner(TrackEvents.ANNOTATE_VIDEO_SAVED, { tools: trackTools.value });

    viewerItemStore.setItem(payload?.data?.item);
    window.onbeforeunload = null;
    trackTools.value = [];

    router.push({ name: 'viewerMain' });
  });

  editor.on('tool_selected', (name: string) => {
    if (!trackTools.value || !Array.isArray(trackTools.value)) {
      trackTools.value = [];
    }

    if (!trackTools.value.includes(name)) {
      trackTools.value.push(name);
    }
  });

  editor.on('dirty', (dirtyState: boolean) => (isDirty.value = dirtyState));
  editor.on('close', () => {
    trackItemOwner(TrackEvents.ANNOTATE_CANCELED);
    window.onbeforeunload = null;

    router.push({ name: 'viewerMain' });
  });

  editor.on('revert', () => trackItemOwner(TrackEvents.ANNOTATE_REVERTED));
};

watch(isDirty, (val: boolean) => {
  if (val && !window.onbeforeunload) {
    window.onbeforeunload = () => 'You have unsaved changes';
    return;
  }

  window.onbeforeunload = null;
});

onMounted(() => {
  if (editor) {
    editor.destroy();
    editor = null;
  }

  bindEditor();
});

onUnmounted(() => {
  isDirty.value = false;
  trackTools.value = [];

  editor.destroy();
  editor = null;
});
</script>
